/** Lesson plan editor (STORY-051): AI drafts a timed structure from the lesson's objectives and
 * bank items; the teacher edits segments, saves versions and approves the one to teach from. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { TeacherChrome } from "../teacher/Chrome";
import {
  approvePlan, generatePlan, getAllocation, getCourse, getHostInfo, getItems, isOfflineError, listPlans, noServerHere, planGeneration, savePlan,
  type PlanBody, type PlanOut, type Segment,
} from "../teacher/api";
import { Button, DraftChip } from "../ui";

const KINDS = ["intro", "teach", "check", "discuss", "practice", "wrap_up"] as const;

export function LessonPlan() {
  const { courseId, lessonId } = useParams({ strict: false }) as { courseId: string; lessonId: string };
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const allocation = useQuery({ queryKey: ["allocation", courseId], queryFn: () => getAllocation(courseId) });
  const items = useQuery({ queryKey: ["items", courseId], queryFn: () => getItems(courseId) });
  const plans = useQuery({ queryKey: ["plans", lessonId], queryFn: () => listPlans(lessonId) });
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false });
  const noAi = noServerHere(host.data);
  const lesson = allocation.data?.lessons.find((l) => l.lesson_id === lessonId) ?? null;
  const lessonIndex = allocation.data ? allocation.data.lessons.findIndex((l) => l.lesson_id === lessonId) : -1;
  const allocated = (course.data?.los ?? []).filter((lo) => lesson?.lo_ids.includes(lo.lo_id));
  const bank = (items.data ?? []).filter((it) => it.lo_id && lesson?.lo_ids.includes(it.lo_id));
  const loCode = (id: string) => course.data?.los.find((l) => l.lo_id === id)?.code ?? id;

  const [editing, setEditing] = useState<PlanBody | null>(null);
  const [base, setBase] = useState<PlanOut | null>(null);
  const [gen, setGen] = useState<{ job: string | null; error: string | null }>({ job: null, error: null });
  const latest = plans.data?.[0] ?? null;
  const approved = plans.data?.find((p) => p.status === "approved") ?? null;

  useEffect(() => {
    if (!editing && latest) {
      setBase(latest);
      setEditing(structuredClone(latest.plan));
    }
  }, [editing, latest]);

  // poll a running generation
  useEffect(() => {
    if (!gen.job) return;
    const job = gen.job;
    const id = window.setInterval(async () => {
      try {
        const g = await planGeneration(lessonId, job);
        if (g.status === "done" || g.status === "failed") {
          window.clearInterval(id);
          setGen({ job: null, error: g.status === "failed" ? (g.error ?? "failed") : null });
          await qc.invalidateQueries({ queryKey: ["plans", lessonId] });
          if (g.plan) {
            setBase(g.plan);
            setEditing(structuredClone(g.plan.plan));
          }
        }
      } catch (e) {
        window.clearInterval(id);
        setGen({ job: null, error: String(e) });
      }
    }, 2500);
    return () => window.clearInterval(id);
  }, [gen.job, lessonId, qc]);

  const generate = useMutation({
    mutationFn: () => generatePlan(lessonId),
    onSuccess: (g) => setGen({ job: g.job_id, error: null }),
    onError: (e) =>
      setGen({
        job: null,
        error:
          e instanceof ApiError && e.code === "no_objectives"
            ? t("lessonPlan.noObjectives")
            : isOfflineError(e) || (e instanceof ApiError && e.code === "ai_unavailable")
              ? t("plan.needsAi")
              : String(e),
      }),
  });
  const save = useMutation({
    mutationFn: (body: PlanBody) => savePlan(lessonId, body),
    onSuccess: (p) => {
      setBase(p);
      setEditing(structuredClone(p.plan));
      void qc.invalidateQueries({ queryKey: ["plans", lessonId] });
    },
  });
  const approve = useMutation({
    mutationFn: (planId: string) => approvePlan(planId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["plans", lessonId] }),
  });

  const dirty = !!editing && !!base && JSON.stringify(editing) !== JSON.stringify(base.plan);
  const total = editing?.segments.reduce((n, s) => n + s.minutes, 0) ?? 0;
  const fmt = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  const setSeg = (i: number, patch: Partial<Segment>) =>
    setEditing((e) => (e ? { ...e, segments: e.segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) } : e));
  const move = (i: number, d: -1 | 1) =>
    setEditing((e) => {
      if (!e) return e;
      const j = i + d;
      if (j < 0 || j >= e.segments.length) return e;
      const segs = [...e.segments];
      const a = segs[i];
      const b = segs[j];
      if (!a || !b) return e;
      segs[i] = b;
      segs[j] = a;
      return { ...e, segments: segs };
    });
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";

  return (
    <TeacherChrome title={lesson ? `${lessonIndex >= 0 ? `${lessonIndex + 1} · ` : ""}${fmt(lesson.date)}${lesson.title ? ` · ${lesson.title}` : ""}` : t("lessonPlan.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="plan" wide>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-[color:var(--ink-2)]">
            <Link to="/t/c/$courseId/plan" params={{ courseId }} className="underline">{t("lessonPlan.backToPlan")}</Link>
          </p>
          <p className="measure mt-1 flex flex-wrap gap-1 text-sm">
            {allocated.map((lo) => (
              <span key={lo.lo_id} className="rounded-full border px-2 py-0.5 text-xs"><span className="mr-1 font-mono">{lo.code}</span>{lo.text}</span>
            ))}
            {allocated.length === 0 && <span className="text-[color:var(--ink-2)]">{t("lessonPlan.noObjectives")}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" aria-describedby={noAi ? "no-ai" : undefined} loading={generate.isPending || !!gen.job} disabled={allocated.length === 0 || noAi} onClick={() => generate.mutate()}>
            {gen.job ? t("lessonPlan.generating") : t("lessonPlan.generate")}
          </Button>
          {editing && (
            <Button loading={save.isPending} disabled={!dirty && !!base} onClick={() => save.mutate(editing)}>{t("lessonPlan.save")}</Button>
          )}
          {base && base.status !== "approved" && !dirty && (
            <Button variant="secondary" loading={approve.isPending} onClick={() => approve.mutate(base.plan_id)}>{t("lessonPlan.approve")}</Button>
          )}
        </div>
      </header>
      {noAi && <p id="no-ai" className="text-sm text-[color:var(--ink-2)]">{t("plan.needsAi")}</p>}
      {gen.error && <p role="alert" className="text-sm text-[color:var(--not-yet)]">{gen.error}</p>}
      {save.error && <p role="alert" className="text-sm text-[color:var(--not-yet)]">{String(save.error)}</p>}

      {!editing && plans.data && plans.data.length === 0 && (
        <section className={card}>
          <p className="text-sm text-[color:var(--ink-2)]">{t("lessonPlan.empty")}</p>
          <p className="mt-2">
            <button type="button" className="underline" onClick={() => { setBase(null); setEditing({ segments: [{ title: t("lessonPlan.kinds.intro"), kind: "intro", minutes: 5, lo_ids: [], item_ids: [], notes: "" }], notes: "" }); }}>
              {t("lessonPlan.startBlank")}
            </button>
          </p>
        </section>
      )}

      {editing && (
        <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
          <section className="flex flex-col gap-3">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              {base?.source === "ai" && base.status !== "approved" && <DraftChip />}
              {base && <span className="text-[color:var(--ink-2)]">{t("lessonPlan.version", { n: base.version })}</span>}
              {base?.status === "approved" && <span className="rounded-full bg-[color:var(--secure)] px-2 py-0.5 text-xs text-[color:var(--accent-ink)]">{t("lessonPlan.approved")}</span>}
              <span className="ml-auto font-medium">{t("lessonPlan.total", { n: total })}</span>
            </p>
            {base?.warnings.map((w) => <p key={w} className="text-xs text-[color:var(--ink-2)]">{w}</p>)}
            <ol className="flex flex-col gap-3">
              {editing.segments.map((s, i) => (
                <li key={i} className={card}>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="bignum">{i + 1}</span>
                    <input className="field field-sm min-w-48 flex-1" aria-label={t("lessonPlan.segTitle")} value={s.title} onChange={(e) => setSeg(i, { title: e.target.value })} />
                    <select className="field field-sm" aria-label={t("lessonPlan.kind")} value={s.kind} onChange={(e) => setSeg(i, { kind: e.target.value })}>
                      {KINDS.map((k) => <option key={k} value={k}>{t(`lessonPlan.kinds.${k}`)}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-sm">
                      <input type="number" min={1} max={120} className="field field-sm w-16" aria-label={t("lessonPlan.minutes")} value={s.minutes} onChange={(e) => setSeg(i, { minutes: Math.max(1, Number(e.target.value) || 1) })} />
                      {t("lessonPlan.min")}
                    </label>
                    <span className="ml-auto flex gap-1">
                      <Button variant="tertiary" size="sm" aria-label={t("lessonPlan.up")} onClick={() => move(i, -1)}>↑</Button>
                      <Button variant="tertiary" size="sm" aria-label={t("lessonPlan.down")} onClick={() => move(i, 1)}>↓</Button>
                      <Button variant="tertiary" size="sm" aria-label={t("lessonPlan.removeSeg")} onClick={() => setEditing((e) => (e ? { ...e, segments: e.segments.filter((_, j) => j !== i) } : e))}>×</Button>
                    </span>
                  </div>
                  <div className="mb-2 flex flex-wrap gap-1">
                    {allocated.map((lo) => (
                      <button
                        key={lo.lo_id}
                        type="button"
                        aria-pressed={s.lo_ids.includes(lo.lo_id)}
                        className={`rounded-full border px-2 py-0.5 text-xs ${s.lo_ids.includes(lo.lo_id) ? "bg-[color:var(--accent)] text-[color:var(--accent-ink)]" : ""}`}
                        onClick={() => setSeg(i, { lo_ids: toggle(s.lo_ids, lo.lo_id) })}
                      >
                        {lo.code}
                      </button>
                    ))}
                  </div>
                  {(s.kind === "check" || s.item_ids.length > 0) && (
                    <div className="mb-2 flex flex-col gap-1 text-sm">
                      {bank.filter((it) => s.lo_ids.length === 0 || (it.lo_id && s.lo_ids.includes(it.lo_id)) || s.item_ids.includes(it.item_id)).map((it) => (
                        <label key={it.item_id} className="flex items-start gap-2">
                          <input type="checkbox" checked={s.item_ids.includes(it.item_id)} onChange={() => setSeg(i, { item_ids: toggle(s.item_ids, it.item_id) })} />
                          <span className="flex flex-wrap items-center gap-1">
                            <span className="font-mono text-xs">{it.lo_id ? loCode(it.lo_id) : ""}</span>
                            {it.stem}
                            {it.review_status === "draft" && <DraftChip kind={it.source === "ai" ? "draft" : "edited"} />}
                          </span>
                        </label>
                      ))}
                      {bank.length === 0 && <span className="text-[color:var(--ink-2)]">{t("lessonPlan.noItems")}</span>}
                    </div>
                  )}
                  <textarea className="field w-full text-sm" rows={2} aria-label={t("lessonPlan.notes")} placeholder={t("lessonPlan.notes")} value={s.notes} onChange={(e) => setSeg(i, { notes: e.target.value })} />
                </li>
              ))}
            </ol>
            <p>
              <button type="button" className="underline" onClick={() => setEditing((e) => (e ? { ...e, segments: [...e.segments, { title: "", kind: "teach", minutes: 10, lo_ids: [], item_ids: [], notes: "" }] } : e))}>
                {t("lessonPlan.addSeg")}
              </button>
            </p>
          </section>
          <aside className="flex flex-col gap-3">
            <section className={card}>
              <h2 className="mb-2 font-semibold">{t("lessonPlan.versions")}</h2>
              <ul className="flex flex-col gap-1 text-sm">
                {plans.data?.map((p) => (
                  <li key={p.plan_id} className="flex flex-wrap items-center gap-2">
                    <button type="button" className={`underline ${base?.plan_id === p.plan_id ? "font-semibold" : ""}`} onClick={() => { setBase(p); setEditing(structuredClone(p.plan)); }}>
                      {t("lessonPlan.version", { n: p.version })}
                    </button>
                    <span className="text-xs text-[color:var(--ink-2)]">{p.source === "ai" ? t("lessonPlan.sourceAi") : t("lessonPlan.sourceTeacher")}</span>
                    {p.status === "approved" && <span className="text-xs">✓ {t("lessonPlan.approved")}</span>}
                  </li>
                ))}
              </ul>
              {approved && <p className="mt-2 text-xs text-[color:var(--ink-2)]">{t("lessonPlan.liveFollows", { n: approved.version })}</p>}
            </section>
          </aside>
        </div>
      )}
    </TeacherChrome>
  );
}
